import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconDownload } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useAccounts } from './accountsApi';
import {
  downloadAccountingExcel,
  useAccountLedger,
  useAging,
  useIncomeStatement,
  useTrialBalance,
} from './accountingReportsApi';

// Phase Accounting-7/8: التقارير المحاسبية. The drill-down chain from
// the UX blueprint: trial-balance row click → ledger tab pre-filtered
// to that account.

export function AccountingReportsPage() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<string | null>('trial-balance');
  const [asOf, setAsOf] = useState<Date | null>(new Date());
  const [range, setRange] = useState<[Date | null, Date | null]>([startOfMonth(), new Date()]);
  const [ledgerAccountId, setLedgerAccountId] = useState<string | null>(null);

  const accounts = useAccounts(true);
  const asOfIso = asOf ? toIsoDate(asOf) : toIsoDate(new Date());
  const fromIso = range[0] ? toIsoDate(range[0]) : toIsoDate(startOfMonth());
  const toIso = range[1] ? toIsoDate(range[1]) : toIsoDate(new Date());

  const tb = useTrialBalance(asOfIso);
  const ledger = useAccountLedger(ledgerAccountId, fromIso, toIso);
  const pnl = useIncomeStatement(fromIso, toIso, null);

  const lang = i18n.language;
  const accountName = (nameAr: string, nameEn: string | null) =>
    lang === 'ar' ? nameAr : (nameEn ?? nameAr);

  const accountOptions = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.isPostable)
        .map((a) => ({
          value: a.accountId,
          label: `${a.code} — ${accountName(a.nameAr, a.nameEn)}`,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts.data, lang],
  );

  const drillIntoLedger = (accountId: string) => {
    setLedgerAccountId(accountId);
    setTab('ledger');
  };

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.reports.title')}
        subtitle={t('accounting.reports.subtitle')}
      />

      <Tabs value={tab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab value="trial-balance">{t('accounting.reports.trialBalance.tab')}</Tabs.Tab>
          <Tabs.Tab value="ledger">{t('accounting.reports.ledger.tab')}</Tabs.Tab>
          <Tabs.Tab value="income-statement">{t('accounting.reports.pnl.tab')}</Tabs.Tab>
          <Tabs.Tab value="aging">{t('accounting.reports.aging.tab')}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="trial-balance" pt="md">
          <Stack gap="md">
            <Paper p="md">
              <Group justify="space-between">
                <DatePickerInput
                  label={t('accounting.reports.trialBalance.asOf')}
                  value={asOf}
                  onChange={setAsOf}
                  style={{ minWidth: 200 }}
                />
                <Button
                  variant="light"
                  leftSection={<IconDownload size={16} stroke={1.6} />}
                  onClick={() => downloadAccountingExcel('trial-balance', { asOf: asOfIso })}
                >
                  {t('accounting.reports.exportExcel')}
                </Button>
              </Group>
            </Paper>
            {tb.isLoading ? (
              <Stack align="center" py={48}><Loader size="sm" /></Stack>
            ) : (
              <Paper p={0}>
                <Table verticalSpacing="sm" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('accounting.chart.columns.code')}</Table.Th>
                      <Table.Th>{t('accounting.chart.columns.account')}</Table.Th>
                      <Table.Th style={{ textAlign: 'end' }}>{t('accounting.journal.columns.debit')}</Table.Th>
                      <Table.Th style={{ textAlign: 'end' }}>{t('accounting.journal.columns.credit')}</Table.Th>
                      <Table.Th style={{ textAlign: 'end' }}>{t('accounting.reports.trialBalance.balance')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(tb.data?.rows ?? []).map((r) => (
                      <Table.Tr
                        key={r.accountId}
                        style={{ cursor: 'pointer' }}
                        onClick={() => drillIntoLedger(r.accountId)}
                      >
                        <Table.Td className="font-numeric">{r.code}</Table.Td>
                        <Table.Td>{accountName(r.nameAr, r.nameEn)}</Table.Td>
                        <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                          {r.totalDebit.toFixed(2)}
                        </Table.Td>
                        <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                          {r.totalCredit.toFixed(2)}
                        </Table.Td>
                        <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                          {r.balance.toFixed(2)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                  {tb.data && (
                    <Table.Tfoot>
                      <Table.Tr>
                        <Table.Td />
                        <Table.Td><Text fw={700}>{t('accounting.reports.total')}</Text></Table.Td>
                        <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 700 }}>
                          {tb.data.totalDebit.toFixed(2)}
                        </Table.Td>
                        <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 700 }}>
                          {tb.data.totalCredit.toFixed(2)}
                        </Table.Td>
                        <Table.Td />
                      </Table.Tr>
                    </Table.Tfoot>
                  )}
                </Table>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="ledger" pt="md">
          <Stack gap="md">
            <Paper p="md">
              <Group>
                <Select
                  label={t('accounting.reports.ledger.accountLabel')}
                  data={accountOptions}
                  value={ledgerAccountId}
                  onChange={setLedgerAccountId}
                  searchable
                  style={{ minWidth: 300 }}
                />
                <DatePickerInput
                  type="range"
                  label={t('reports.dateRange')}
                  value={range}
                  onChange={setRange}
                  style={{ minWidth: 260 }}
                />
              </Group>
            </Paper>
            {ledgerAccountId === null ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                {t('accounting.reports.ledger.pickAccount')}
              </Text>
            ) : ledger.isLoading ? (
              <Stack align="center" py={48}><Loader size="sm" /></Stack>
            ) : ledger.data ? (
              <Paper p={0}>
                <Table verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('accounting.journal.columns.date')}</Table.Th>
                      <Table.Th>{t('accounting.journal.columns.number')}</Table.Th>
                      <Table.Th>{t('accounting.journal.columns.description')}</Table.Th>
                      <Table.Th style={{ textAlign: 'end' }}>{t('accounting.journal.columns.debit')}</Table.Th>
                      <Table.Th style={{ textAlign: 'end' }}>{t('accounting.journal.columns.credit')}</Table.Th>
                      <Table.Th style={{ textAlign: 'end' }}>{t('accounting.reports.ledger.runningBalance')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    <Table.Tr>
                      <Table.Td colSpan={5}>
                        <Text size="sm" fw={600}>{t('accounting.reports.ledger.opening')}</Text>
                      </Table.Td>
                      <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 600 }}>
                        {ledger.data.openingBalance.toFixed(2)}
                      </Table.Td>
                    </Table.Tr>
                    {ledger.data.rows.map((r) => (
                      <Table.Tr key={`${r.journalEntryId}-${r.runningBalance}`}>
                        <Table.Td className="font-numeric">{r.entryDate}</Table.Td>
                        <Table.Td className="font-numeric">{r.entryNumber}</Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>{r.memo ?? r.description ?? '—'}</Text>
                        </Table.Td>
                        <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                          {r.debit > 0 ? r.debit.toFixed(2) : ''}
                        </Table.Td>
                        <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                          {r.credit > 0 ? r.credit.toFixed(2) : ''}
                        </Table.Td>
                        <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                          {r.runningBalance.toFixed(2)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    <Table.Tr>
                      <Table.Td colSpan={5}>
                        <Text size="sm" fw={700}>{t('accounting.reports.ledger.closing')}</Text>
                      </Table.Td>
                      <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 700 }}>
                        {ledger.data.closingBalance.toFixed(2)}
                      </Table.Td>
                    </Table.Tr>
                  </Table.Tbody>
                </Table>
              </Paper>
            ) : null}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="income-statement" pt="md">
          <Stack gap="md">
            <Paper p="md">
              <Group justify="space-between">
                <DatePickerInput
                  type="range"
                  label={t('reports.dateRange')}
                  value={range}
                  onChange={setRange}
                  style={{ minWidth: 260 }}
                />
                <Button
                  variant="light"
                  leftSection={<IconDownload size={16} stroke={1.6} />}
                  onClick={() =>
                    downloadAccountingExcel('income-statement', { from: fromIso, to: toIso })
                  }
                >
                  {t('accounting.reports.exportExcel')}
                </Button>
              </Group>
            </Paper>
            {pnl.isLoading ? (
              <Stack align="center" py={48}><Loader size="sm" /></Stack>
            ) : pnl.data ? (
              <Paper p="md">
                <Stack gap="lg">
                  <Section
                    title={t('accounting.reports.pnl.revenue')}
                    lines={pnl.data.revenue}
                    total={pnl.data.totalRevenue}
                    color="green"
                    nameOf={accountName}
                    onDrill={drillIntoLedger}
                  />
                  <Section
                    title={t('accounting.reports.pnl.expenses')}
                    lines={pnl.data.expenses}
                    total={pnl.data.totalExpenses}
                    color="red"
                    nameOf={accountName}
                    onDrill={drillIntoLedger}
                  />
                  <Group justify="space-between" p="sm"
                    style={{ borderTop: '2px solid var(--wheels-border)' }}>
                    <Text fw={700}>{t('accounting.reports.pnl.netIncome')}</Text>
                    <Text fw={700} size="lg" className="font-numeric"
                      c={pnl.data.netIncome >= 0 ? 'green' : 'red'}>
                      {pnl.data.netIncome.toFixed(2)}
                    </Text>
                  </Group>
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="aging" pt="md">
          <AgingTab asOfIso={asOfIso} asOf={asOf} setAsOf={setAsOf} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function AgingTab({
  asOfIso, asOf, setAsOf,
}: { asOfIso: string; asOf: Date | null; setAsOf: (d: Date | null) => void }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<'1' | '2'>('1');
  const aging = useAging(Number(kind) as 1 | 2, asOfIso);

  return (
    <Stack gap="md">
      <Paper p="md">
        <Group>
          <SegmentedControl
            value={kind}
            onChange={(v) => setKind(v as '1' | '2')}
            data={[
              { value: '1', label: t('accounting.reports.aging.receivables') },
              { value: '2', label: t('accounting.reports.aging.payables') },
            ]}
          />
          <DatePickerInput
            label={t('accounting.reports.trialBalance.asOf')}
            value={asOf}
            onChange={setAsOf}
            style={{ minWidth: 200 }}
          />
        </Group>
      </Paper>
      {aging.isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.reports.aging.party')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.reports.aging.current')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>31–60</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>61–90</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>+90</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.reports.total')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(aging.data?.rows ?? []).map((r) => (
                <Table.Tr key={r.partyId}>
                  <Table.Td>{r.partyName}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{r.current.toFixed(2)}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{r.days31To60.toFixed(2)}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{r.days61To90.toFixed(2)}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{r.over90.toFixed(2)}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 600 }}>{r.total.toFixed(2)}</Table.Td>
                </Table.Tr>
              ))}
              {aging.data && aging.data.rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      {t('accounting.reports.aging.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
            {aging.data && aging.data.rows.length > 0 && (
              <Table.Tfoot>
                <Table.Tr>
                  <Table.Td><Text fw={700}>{t('accounting.reports.total')}</Text></Table.Td>
                  <Table.Td colSpan={4} />
                  <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 700 }}>
                    {aging.data.total.toFixed(2)}
                  </Table.Td>
                </Table.Tr>
              </Table.Tfoot>
            )}
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

function Section({
  title, lines, total, color, nameOf, onDrill,
}: {
  title: string;
  lines: { accountId: string; code: string; nameAr: string; nameEn: string | null; amount: number }[];
  total: number;
  color: string;
  nameOf: (ar: string, en: string | null) => string;
  onDrill: (accountId: string) => void;
}) {
  return (
    <Stack gap={4}>
      <Badge variant="light" color={color} radius="sm">{title}</Badge>
      {lines.map((l) => (
        <Group
          key={l.accountId}
          justify="space-between"
          px="sm"
          py={4}
          style={{ cursor: 'pointer' }}
          onClick={() => onDrill(l.accountId)}
        >
          <Text size="sm">
            <span className="font-numeric">{l.code}</span> {nameOf(l.nameAr, l.nameEn)}
          </Text>
          <Text size="sm" className="font-numeric">{l.amount.toFixed(2)}</Text>
        </Group>
      ))}
      <Group justify="space-between" px="sm" pt={4}
        style={{ borderTop: '1px solid var(--wheels-border)' }}>
        <Text size="sm" fw={600}>{title}</Text>
        <Text size="sm" fw={600} className="font-numeric">{total.toFixed(2)}</Text>
      </Group>
    </Stack>
  );
}

function startOfMonth(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
